import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";

const AddUpdateUnitModal = ({
  show,
  onHide,
  onSubmit,
  isEdit,
  unit,
}) => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const [formData, setFormData] = useState({
    measureName: "",
    measurement: 1.0,
    standardUnitName: "",
    symbol: "",
    measurementTypeName: null,
  });
  const [errors, setErrors] = useState({});
  const [standardUnitOptions, setStandardUnitOptions] = useState([]);

  const measurementTypeOptions = [
    { value: "Unit", label: "Unit" },
    { value: "Weight", label: "Weight" },
    { value: "Volume", label: "Volume" },
  ];

  // Standard unit options based on measurement type
  const getStandardUnitOptions = (measurementType) => {
    if (!measurementType) return [];
    
    switch (measurementType) {
      case "Weight":
        return [
          { value: "Kilogram", label: "Kilogram" },
          { value: "Gram", label: "Gram" },
          { value: "Pound", label: "Pound" },
          { value: "Ounce", label: "Ounce" },
        ];
      case "Volume":
        return [
          { value: "Liter", label: "Liter" },
          { value: "Milliliter", label: "Milliliter" },
          { value: "Fluid Ounce", label: "Fluid Ounce" },
        ];
      case "Unit":
      default:
        return [
          { value: "Piece", label: "Piece" },
          { value: "Box", label: "Box" },
          { value: "Pack", label: "Pack" },
        ];
    }
  };

  useEffect(() => {
    if (isEdit && unit) {
      setFormData({
        measureName: unit.measureName || "",
        measurement: unit.measurement || 1.0,
        standardUnitName: unit.standardUnitName || "",
        symbol: unit.symbol || "",
        measurementTypeName: unit.measurementTypeName
          ? { value: unit.measurementTypeName, label: unit.measurementTypeName }
          : null,
      });
      if (unit.measurementTypeName) {
        setStandardUnitOptions(getStandardUnitOptions(unit.measurementTypeName));
      }
    } else {
      setFormData({
        measureName: "",
        measurement: 1.0,
        standardUnitName: "",
        symbol: "",
        measurementTypeName: null,
      });
      setStandardUnitOptions([]);
    }
    setErrors({});
  }, [isEdit, unit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "measurement" ? parseFloat(value) || 0 : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSelectChange = (selectedOption, field) => {
    // When measurement type changes, update standard unit options
    if (field === "measurementTypeName") {
      const options = getStandardUnitOptions(selectedOption?.value);
      setStandardUnitOptions(options);
      
      setFormData((prev) => {
        const updatedData = {
          ...prev,
          [field]: selectedOption,
        };
        
        // Reset standard unit name if it's not in the new options
        if (prev.standardUnitName) {
          const currentStandardUnit = options.find(
            (opt) => opt.value === prev.standardUnitName
          );
          if (!currentStandardUnit && options.length > 0) {
            updatedData.standardUnitName = options[0].value;
          }
        } else if (options.length > 0) {
          updatedData.standardUnitName = options[0].value;
        }
        
        return updatedData;
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: selectedOption,
      }));
    }

    // Clear error when user makes selection
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.measureName.trim()) {
      newErrors.measureName = "Unit name is required";
    }

    if (!formData.measurementTypeName) {
      newErrors.measurementTypeName = "Measurement type is required";
    }

    if (!formData.standardUnitName) {
      newErrors.standardUnitName = "Standard unit is required";
    }

    if (formData.measurement <= 0) {
      newErrors.measurement = "Measurement must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      measureID: unit ? unit.measureID : 0,
      measureName: formData.measureName.trim(),
      measurement: parseFloat(formData.measurement) || 1.0,
      standardUnitName: formData.standardUnitName,
      symbol: formData.symbol.trim() || null,
      measurementTypeName: formData.measurementTypeName?.value || null,
    };
    onSubmit(submitData, isEdit, false);
  };

  const handleClose = () => {
    setFormData({
      measureName: "",
      measurement: 1.0,
      standardUnitName: "",
      symbol: "",
      measurementTypeName: null,
    });
    setStandardUnitOptions([]);
    setErrors({});
    onHide();
  };

  const standardUnitSelectValue = standardUnitOptions.find(
    (opt) => opt.value === formData.standardUnitName
  );

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static" keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? "Edit Unit" : "Add New Unit"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Unit Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="measureName"
                  value={formData.measureName}
                  onChange={handleInputChange}
                  placeholder="e.g., Kilogram, Gram, Liter"
                  isInvalid={!!errors.measureName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.measureName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Measurement Type <span className="text-danger">*</span>
                </Form.Label>
                <Select
                  value={formData.measurementTypeName}
                  onChange={(val) => handleSelectChange(val, "measurementTypeName")}
                  options={measurementTypeOptions}
                  styles={isDarkMode ? DropDownStylesDark : DropDownStyles}
                  placeholder="Select measurement type"
                  isInvalid={!!errors.measurementTypeName}
                />
                {errors.measurementTypeName && (
                  <div className="text-danger small mt-1">
                    {errors.measurementTypeName}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Standard Unit <span className="text-danger">*</span>
                </Form.Label>
                <Select
                  value={standardUnitSelectValue}
                  onChange={(val) =>
                    setFormData((prev) => ({
                      ...prev,
                      standardUnitName: val?.value || "",
                    }))
                  }
                  options={standardUnitOptions}
                  styles={isDarkMode ? DropDownStylesDark : DropDownStyles}
                  placeholder="Select standard unit"
                  isDisabled={!formData.measurementTypeName}
                  isInvalid={!!errors.standardUnitName}
                />
                {errors.standardUnitName && (
                  <div className="text-danger small mt-1">
                    {errors.standardUnitName}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Symbol</Form.Label>
                <Form.Control
                  type="text"
                  name="symbol"
                  value={formData.symbol}
                  onChange={handleInputChange}
                  placeholder="e.g., kg, L, pcs"
                  maxLength={20}
                />
                <Form.Text className="text-muted">
                  Symbol to display in frontend (optional)
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Measurement Value <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="measurement"
                  value={formData.measurement}
                  onChange={handleInputChange}
                  placeholder="1.0"
                  step="0.001"
                  min="0.001"
                  isInvalid={!!errors.measurement}
                />
                <Form.Text className="text-muted">
                  Conversion factor relative to standard unit
                </Form.Text>
                <Form.Control.Feedback type="invalid">
                  {errors.measurement}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          style={{
            backgroundColor: themeColor,
            borderColor: themeColor,
          }}
        >
          {isEdit ? "Update Unit" : "Add Unit"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddUpdateUnitModal;

